import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Search, Star, MapPin, Briefcase, Loader2, Filter, ChevronDown, Users, Clock, Sparkles, CheckCircle, Shield, Fingerprint, FileCheck
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";

// AI Matching message component
const AIMatchingMessage = ({ count, category, location }) => {
  if (count === 0) return null;
  
  const displayCount = Math.min(count, 3);
  const categoryText = category ? ` who specialize in ${category}` : "";
  const locationText = location ? ` near ${location}` : "";
  
  return (
    <div className="bg-gradient-to-r from-[#1E40AF]/10 to-purple-500/10 border border-[#1E40AF]/20 rounded-xl p-4 mb-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[#1E40AF]/20 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-[#1E40AF]" />
        </div>
        <div>
          <p className="font-semibold text-gray-900 mb-1">
            We found {displayCount} professional{displayCount > 1 ? 's' : ''}{categoryText}
          </p>
          <p className="text-gray-600 text-sm">
            {count <= 3 
              ? `These verified pros are ready to help you${locationText}. Post a job to get quotes!`
              : `Plus ${count - 3} more verified professionals available${locationText}. Post a job to get quotes!`
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default function BrowseProfessionalsPage() {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");
  
  const [professionals, setProfessionals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(categoryParam || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchProfessionals = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append("category", selectedCategory);
      if (searchQuery) params.append("location", searchQuery);
      if (verifiedOnly) params.append("verified_only", "true");
      params.append("limit", "20");
      
      const response = await axios.get(`${API}/professionals/public?${params.toString()}`);
      setProfessionals(response.data.professionals);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Failed to load professionals");
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchQuery, verifiedOnly]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        // Categories are optional - page still works without them
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProfessionals();
  }, [fetchProfessionals]);

  useEffect(() => {
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [categoryParam]);

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-7xl mx-auto" data-testid="browse-professionals-page">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-gray-900 mb-4">
            Find Trusted Professionals
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Browse verified professionals ready to help with your projects. All pros are background-checked and reviewed by customers.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-4 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search by location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchProfessionals()}
                className="pl-12 h-12 rounded-xl"
                data-testid="location-search"
              />
            </div>
            
            <Select value={selectedCategory || "all"} onValueChange={(val) => setSelectedCategory(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full md:w-64 h-12 rounded-xl" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              onClick={fetchProfessionals} 
              className="h-12 px-6 rounded-xl bg-[#1E40AF] hover:bg-[#1E40AF]/90"
            >
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>

          {/* Verified Filter Toggle */}
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl border border-emerald-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <Label htmlFor="verified-filter" className="font-semibold text-gray-900 cursor-pointer">
                  Show Verified Pros Only
                </Label>
                <p className="text-sm text-gray-600">
                  Verified pros get <span className="font-semibold text-emerald-600">priority access</span> to more jobs
                </p>
              </div>
            </div>
            <Switch
              id="verified-filter"
              checked={verifiedOnly}
              onCheckedChange={setVerifiedOnly}
              data-testid="verified-filter-toggle"
              className="data-[state=checked]:bg-emerald-600"
            />
          </div>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-gray-600">
            {loading ? "Searching..." : `${total} professional${total !== 1 ? 's' : ''} found`}
            {selectedCategory && ` in ${getCategoryName(selectedCategory)}`}
            {verifiedOnly && (
              <span className="ml-1 text-emerald-600 font-medium">(verified only)</span>
            )}
          </p>
          {verifiedOnly && (
            <Badge className="bg-emerald-100 text-emerald-700">
              <Shield className="w-3 h-3 mr-1" />
              Verified Filter Active
            </Badge>
          )}
        </div>

        {/* AI Matching Message */}
        {!loading && professionals.length > 0 && (
          <AIMatchingMessage 
            count={total} 
            category={getCategoryName(selectedCategory)} 
            location={searchQuery}
          />
        )}

        {/* Professionals Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#1E40AF]" />
          </div>
        ) : professionals.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No professionals found</h3>
            <p className="text-gray-500 mb-4">Try adjusting your filters or search criteria</p>
            <Button onClick={() => { setSelectedCategory(""); setSearchQuery(""); setVerifiedOnly(false); }}>
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {professionals.map((pro) => (
              <Card key={pro.id} className="hover:shadow-lg transition-shadow" data-testid={`professional-card-${pro.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="w-16 h-16 rounded-full bg-[#1E40AF] flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {pro.profile_image ? (
                        <img src={pro.profile_image} alt={pro.full_name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white text-xl font-bold">
                          {pro.full_name?.charAt(0) || "P"}
                        </span>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 truncate">{pro.full_name}</h3>
                        {/* ID Verified Badge with Priority Access */}
                        {pro.identity_verified && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs" data-testid={`id-verified-badge-${pro.id}`}>
                            <Fingerprint className="w-3 h-3 mr-1" />
                            ID Verified
                          </Badge>
                        )}
                        {/* Background Check Badge */}
                        {pro.background_check_status === "approved" && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs" data-testid={`bg-check-badge-${pro.id}`}>
                            <FileCheck className="w-3 h-3 mr-1" />
                            BG Checked
                          </Badge>
                        )}
                      </div>
                      {pro.business_name && (
                        <p className="text-sm text-gray-600 truncate">{pro.business_name}</p>
                      )}
                      
                      {/* Priority Access indicator for verified pros */}
                      {(pro.identity_verified || pro.background_check_status === "approved") && (
                        <p className="text-xs text-emerald-600 font-medium mt-1">
                          Priority job access
                        </p>
                      )}
                      
                      {/* Rating */}
                      <div className="flex items-center gap-1 mt-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <span className="text-sm font-medium">
                          {pro.average_rating?.toFixed(1) || "New"}
                        </span>
                        {pro.review_count > 0 && (
                          <span className="text-sm text-gray-500">({pro.review_count} reviews)</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Location */}
                  {(pro.location || pro.service_area) && (
                    <div className="flex items-center gap-2 mt-4 text-sm text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{pro.location || pro.service_area}</span>
                    </div>
                  )}
                  
                  {/* Categories */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {pro.categories?.slice(0, 3).map((cat, idx) => (
                      <Badge key={`${pro.id}-${idx}`} variant="secondary" className="text-xs">
                        {getCategoryName(cat)}
                      </Badge>
                    ))}
                    {pro.categories?.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{pro.categories.length - 3} more
                      </Badge>
                    )}
                  </div>
                  
                  {/* Bio */}
                  {pro.bio && (
                    <p className="text-sm text-gray-600 mt-3 line-clamp-2">{pro.bio}</p>
                  )}
                  
                  {/* Pricing & Action */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <div>
                      {pro.hourly_rate && (
                        <p className="text-sm">
                          <span className="font-semibold text-gray-900">${pro.hourly_rate}</span>
                          <span className="text-gray-500">/hr</span>
                        </p>
                      )}
                    </div>
                    <Link to={`/professional/${pro.id}`}>
                      <Button size="sm" className="bg-[#1E40AF] hover:bg-[#1E40AF]/90">
                        View Profile
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
